import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { showErrorToast } from '@fff-web/fff-ui-shared/Toast';

import { uploadImageToCdn } from 'pages/MyAccount/services/api/admin-images';
import styles from './AdminImageUpload.module.css';

const DeleteButton = ({ onClick, style }) => (
  <button
    type="button"
    className={styles.deleteButton}
    style={style}
    onClick={onClick}
  >
    <div>
      <i className="fas fa-times" />
    </div>
  </button>
);

const UploadButton = ({ onClick, text, error, style, disabled }) => (
  <button
    type="button"
    className={`${styles.uploadButton} ${error ? styles.error : ''} ${
      disabled ? styles.disabled : ''
    }`}
    onClick={onClick}
    style={style}
  >
    <i className="fal fa-arrow-circle-up" />
    <span>{text}</span>
  </button>
);

const AdminImageUpload = ({
  imageUrl,
  error,
  uploadText,
  labelText,
  updateImageUrl,
  style,
  tooltip,
  validateImage,
  disabled,
  imageType,
  name,
}) => {
  const imageInputRef = useRef(null);

  const removeImage = () => {
    updateImageUrl('');
  };

  const openFileDialog = () => {
    imageInputRef.current.click();
  };

  const uploadToCdn = async (formData) => {
    try {
      const response = await uploadImageToCdn(formData);
      const { value: cdnUrl } = response;
      updateImageUrl(cdnUrl);
    } catch {
      showErrorToast('Image Upload Failed', 'Error during uploading image.');
    }
  };

  const handleFileChange = async (e) => {
    const formData = new FormData();
    const currentImage = e.target.files[0];
    if (currentImage) {
      const imageError = await validateImage(currentImage);
      if (imageError) {
        showErrorToast(imageError.title, imageError.subtitle);
      } else {
        formData.append('file', currentImage);
        uploadToCdn(formData);
      }
    }
  };

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor={name}>
        {labelText}
        {tooltip && (
          <span data-tip={tooltip} data-for="image-upload">
            <i className="fas fa-info-circle" />
          </span>
        )}
      </label>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg, image/png, image/GIF"
        className={styles.fileDialog}
        onChange={handleFileChange}
        name={name}
        disabled={disabled}
      />
      <div className={styles.imageContainer} style={style}>
        {imageUrl ? (
          <>
            <img alt="thumbnail" src={imageUrl} />
            {!disabled && <DeleteButton onClick={removeImage} style={style} />}
          </>
        ) : (
          <UploadButton
            error={error}
            text={uploadText}
            onClick={openFileDialog}
            style={
              imageType === 'banner'
                ? { ...style, flexDirection: 'row' }
                : style
            }
            disabled={disabled}
          />
        )}
      </div>
      <ReactTooltip
        id="image-upload"
        place="top"
        type="dark"
        effect="solid"
        //eslint-disable-next-line
        html={true}
        className={styles.tooltip}
      />
    </div>
  );
};

AdminImageUpload.propTypes = {
  imageUrl: PropTypes.string,
  error: PropTypes.string,
  uploadText: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  updateImageUrl: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  tooltip: PropTypes.string,
  validateImage: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  imageType: PropTypes.string,
};

AdminImageUpload.defaultProps = {
  imageUrl: '',
  error: '',
  uploadText: '',
  style: {},
  tooltip: '',
  disabled: false,
  imageType: '',
};

export default AdminImageUpload;
